import React, { useMemo } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  ExpandableSection,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  InvestigationFieldBreakdown,
  InvestigationSuggestion,
  Stage,
} from "../../lusca-search-api/generated-src";
import { useInvestigationSuggestions } from "../../hooks/useInvestigations";
import { InvestigationRule } from "./InvestigationRule";
import {
  InvestigationRule as Rule,
  MatchingPattern,
  RuleRelation,
  investigationPath,
  matchingPatterns,
  ownerLabel,
  predicatesToNarrow,
  ruleFieldLabel,
} from "./investigationUtils";
import { shortPatternId } from "./trackstarsText";

// ---- R14: TrackStars already identified this population ------------------------------------------

interface TrackStarsPatternHintsProps {
  stage: Stage;
  rule: Rule;
  /** When the builder already starts from a suggestion, that pattern is not re-suggested. */
  currentPatternId?: string;
  /** The pattern adopted from this list (stays visible, marked, undoable). */
  adoptedPatternId?: string;
  /**
   * The investigation this builder splits from: a pattern that case already works is not
   * "already under investigation" by someone else — the split is deliberately a subset of it.
   */
  excludeInvestigationId?: string;
  /**
   * Breakdown of the completed preview of `rule`: patterns it proves select none of the
   * population are dropped (see `ruleMissesPopulation`).
   */
  breakdown?: InvestigationFieldBreakdown[];
  /** Adopt a pattern as the case's origin while keeping the current rule (minus `uncategorized`). */
  onUseStarter: (
    suggestion: InvestigationSuggestion,
    reportId?: string
  ) => void;
  /** Pin the pattern's predicates on top of the current rule, then adopt it as the origin. */
  onNarrowToPattern: (
    suggestion: InvestigationSuggestion,
    reportId?: string
  ) => void;
  /** Undo the adoption (and the narrowing that came with it). */
  onUndoAdopt: () => void;
  /**
   * Rendered inside the preview's collapsed "Overlaps" section: no alert of its own, and patterns
   * already under investigation are left out (that case is listed with the other overlaps).
   */
  embedded?: boolean;
}

export type PatternOwner = {
  investigationId: string;
  title?: string;
  owner?: string | null;
};

export const patternOwner = (
  suggestion: InvestigationSuggestion
): PatternOwner | undefined =>
  suggestion.ownership?.investigations?.find(
    (i) =>
      !!i.investigationId &&
      (i.relation === "origin" || i.relation === "covers")
  ) as PatternOwner | undefined;

/** Whether an active investigation already works this pattern. */
export const isPatternOwned = (suggestion: InvestigationSuggestion): boolean =>
  suggestion.ownership?.state === "owned" && !!patternOwner(suggestion);

/**
 * TrackStars patterns the draft rule can share items with, minus the one the builder started
 * from and any the split parent already works. Shared by the hints and the preview's verdict so
 * both see the same set (the query is cached, so calling it twice costs nothing).
 */
export function useMatchingPatterns(
  stage: Stage,
  rule: Rule,
  currentPatternId?: string,
  excludeInvestigationId?: string,
  breakdown?: InvestigationFieldBreakdown[]
): { matches: MatchingPattern[]; reportId?: string } {
  const suggestions = useInvestigationSuggestions(stage, rule.length > 0);
  const available = suggestions?.data?.suggestions;
  const reportId = suggestions?.data?.report?.reportId;
  const ruleKey = JSON.stringify(rule);
  const matches = useMemo(
    () =>
      matchingPatterns(rule, available, breakdown).filter(
        (m) =>
          m.suggestion.patternId !== currentPatternId &&
          !(
            excludeInvestigationId &&
            m.suggestion.ownership?.investigations?.some(
              (i) => i.investigationId === excludeInvestigationId
            )
          )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the serialised rule
    [ruleKey, available, currentPatternId, excludeInvestigationId, breakdown]
  );
  return { matches, reportId };
}

const RELATION_SENTENCE: Record<RuleRelation, string> = {
  rule_subset: "Your rule is narrower than the pattern.",
  rule_superset: "Your rule is wider than the pattern.",
  rule_overlap: "Your rule overlaps the pattern.",
};

interface PatternRowProps {
  stage: Stage;
  rule: Rule;
  suggestion: InvestigationSuggestion;
  relation: RuleRelation;
  owner?: PatternOwner;
  adopted: boolean;
  onAdopt: () => void;
  onNarrow: () => void;
  onUndo: () => void;
}

/**
 * One pattern, one line of headline, one line saying what the button will do, details folded.
 * The action depends on how the draft relates to the pattern: a draft wider than (or merely
 * overlapping) the pattern is narrowed to it — adopting a pattern as the origin of a case that
 * also covers four other patterns would be a lie — while a draft already within the pattern
 * just adopts it.
 */
const PatternRow: React.FC<PatternRowProps> = ({
  stage,
  rule,
  suggestion,
  relation,
  owner,
  adopted,
  onAdopt,
  onNarrow,
  onUndo,
}) => {
  const id = shortPatternId(suggestion.patternId);
  const derived = (suggestion.derivedRule ?? []) as Rule;
  const toPin = predicatesToNarrow(rule, derived);
  const narrows = toPin.length > 0;
  let action: React.ReactNode;
  let effect: string;
  if (owner) {
    action = (
      <Button
        href={investigationPath(owner.investigationId, stage)}
        target="_blank"
        iconName="external"
        iconAlign="right"
      >
        Open investigation
      </Button>
    );
    effect = `${ownerLabel(owner.owner)} works it as “${
      owner.title ?? owner.investigationId
    }”. Items it already covers will not be re-claimed by a new case.`;
  } else if (adopted) {
    action = (
      <Button variant="normal" onClick={onUndo} iconName="undo">
        Undo
      </Button>
    );
    effect = `Adopted: ${id}'s diagnosis becomes this case's description, ${id} is recorded as its origin, and it stops being suggested to others.`;
  } else if (narrows) {
    action = <Button onClick={onNarrow}>Narrow rule to this pattern</Button>;
    effect = `Pins ${toPin
      .map((p) => ruleFieldLabel(p.field as string).toLowerCase())
      .join(
        ", "
      )} so the case covers only ${id}, and records ${id} as its origin (its diagnosis becomes the description).`;
  } else {
    action = <Button onClick={onAdopt}>Adopt as origin</Button>;
    effect = `Records ${id} as this case's origin and uses its diagnosis as the description. Your rule stays as it is.`;
  }
  return (
    <div
      data-testid="trackstars-pattern-hint"
      data-pattern-id={suggestion.patternId}
      data-adopted={adopted || undefined}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gap: "12px",
        alignItems: "start",
      }}
    >
      <SpaceBetween size="xxs">
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <strong>{id}</strong>
          {suggestion.suggestedCategory && (
            <Badge color="blue">{suggestion.suggestedCategory}</Badge>
          )}
          <Badge>{suggestion.recommendedAction?.type ?? "investigate"}</Badge>
          {adopted && <Badge color="green">adopted as origin</Badge>}
        </SpaceBetween>
        <div
          title={suggestion.description}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {suggestion.description}
        </div>
        <Box color="text-body-secondary" fontSize="body-s">
          {effect}
        </Box>
        <ExpandableSection headerText="Details" variant="footer">
          <SpaceBetween size="xs">
            <div>{suggestion.description}</div>
            {suggestion.rootCause?.summary && (
              <div>
                <strong>Root cause:</strong> {suggestion.rootCause.summary}
              </div>
            )}
            <Box color="text-body-secondary" fontSize="body-s">
              {RELATION_SENTENCE[relation]}
            </Box>
            <div>
              <Box variant="awsui-key-label">Pattern rule</Box>
              <InvestigationRule rule={suggestion.derivedRule} compact />
            </div>
          </SpaceBetween>
        </ExpandableSection>
      </SpaceBetween>
      <div>{action}</div>
    </div>
  );
};

/**
 * One rollup of every TrackStars pattern this population overlaps — open the case that already
 * works it, narrow the rule to it, or adopt it as the origin (R14.1–R14.2). Pinning
 * `uncategorized` is no longer warned about here: the builder captions it, and creation stores
 * it in a form that survives categorisation (R14.1).
 */
export const TrackStarsPatternHints: React.FC<TrackStarsPatternHintsProps> = ({
  stage,
  rule,
  currentPatternId,
  adoptedPatternId,
  excludeInvestigationId,
  breakdown,
  onUseStarter,
  onNarrowToPattern,
  onUndoAdopt,
  embedded,
}) => {
  const { matches, reportId } = useMatchingPatterns(
    stage,
    rule,
    currentPatternId,
    excludeInvestigationId,
    breakdown
  );
  if (!matches.length) return null;

  const owned = matches.filter((m) => isPatternOwned(m.suggestion));
  const free = matches.filter((m) => !owned.includes(m));
  const adoptedMatch = matches.find(
    (m) => m.suggestion.patternId === adoptedPatternId
  );
  const single = matches.length === 1 ? matches[0] : undefined;
  const header = single
    ? owned.length
      ? `TrackStars ${shortPatternId(
          single.suggestion.patternId
        )} is already under investigation`
      : `TrackStars already identified this population as ${shortPatternId(
          single.suggestion.patternId
        )}`
    : `This population overlaps ${matches.length} TrackStars patterns`;
  const summary = single
    ? undefined
    : [
        owned.length && `${owned.length} already under investigation`,
        free.length && `${free.length} not yet claimed`,
      ]
        .filter(Boolean)
        .join(" · ");

  const row = ({ suggestion, relation }: MatchingPattern) => (
    <PatternRow
      key={suggestion.patternId}
      stage={stage}
      rule={rule}
      suggestion={suggestion}
      relation={relation}
      owner={
        suggestion.ownership?.state === "owned"
          ? patternOwner(suggestion)
          : undefined
      }
      adopted={suggestion.patternId === adoptedPatternId}
      onAdopt={() => onUseStarter(suggestion, reportId)}
      onNarrow={() => onNarrowToPattern(suggestion, reportId)}
      onUndo={onUndoAdopt}
    />
  );

  if (embedded) {
    if (!free.length) return null;
    return (
      <SpaceBetween size="s">
        <Box variant="awsui-key-label">
          TrackStars pattern{free.length === 1 ? "" : "s"} nobody works yet
        </Box>
        {free.map(row)}
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="s">
      {matches.length > 0 && (
        <Alert
          type={adoptedMatch ? "success" : owned.length ? "warning" : "info"}
          data-testid="trackstars-pattern-hints"
          header={header}
        >
          <SpaceBetween size="s">
            {summary && (
              <div>
                <Box color="text-body-secondary" fontSize="body-s">
                  {summary}
                </Box>
                <div>
                  Your rule is wide enough to cover several known patterns.
                  Narrow it to the one you mean to work, or open the case that
                  already works it.
                </div>
              </div>
            )}
            {owned.length > 0 && !single && (
              <Box variant="awsui-key-label">Already under investigation</Box>
            )}
            {owned.map(row)}
            {free.length > 0 && !single && (
              <Box variant="awsui-key-label">Not yet claimed</Box>
            )}
            {free.map(row)}
          </SpaceBetween>
        </Alert>
      )}
    </SpaceBetween>
  );
};
